/**
 * The private-domain list and the editor that changes it — the writable half of the
 * surveillance posture.
 *
 * ── Private rules come first, and that is the same ordering an employee sees ──
 *
 * Task 42's `CollectionRules` puts the private list first on `/my-activity` because
 * "an employee who cannot see which domains are private cannot tell whether the
 * promise is being kept". This is the same list from the authoring side, ordered the
 * same way, so that the person writing the rule and the person living under it are
 * looking at the same thing in the same order.
 *
 * ── Why the admin may see it at all ─────────────────────────────────────────
 *
 * Task 49 withheld the alert-rule catalogue from employees on the grounds that it is
 * the company's surveillance posture. The two lists point opposite ways: an alert
 * rule tells a watched person **what trips a tripwire**, so publishing it publishes
 * an evasion manual; the private-domain list tells them **what is never recorded**,
 * which is a promise whose only value is that the protected person can check it.
 * `GET /activities/catalog` already serves it to any `view_*` holder, deliberately.
 * The sentence is on screen, not only here.
 *
 * ── The editor writes nothing ────────────────────────────────────────────────
 *
 * It stages a draft. The save lives behind `DiffSummary`, so the change and its
 * direction are physically between the editor and the button. `match_type` is fixed
 * at the endpoint's default (`exact`) and is not offered: a `regex` rule here would
 * be a company-authored pattern in a matcher, which is the thing Task 35 screens and
 * fails closed on, and offering it beside a domain field invites a URL. The two
 * `match_field` values cover the actual requirement — one host, or a host and its
 * subdomains — and neither can express a path, "which is the point".
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { ActivityCategory, WebsiteCatalogEntry } from '../../api/types';
import type { MessageKey } from '../../i18n/messages';
import type { WebsiteRuleDraft } from '../../policies/diff';
import { Badge } from '../../states/Badge';
import { EmptyState } from '../../states/EmptyState';

const CATEGORY_KEY: Readonly<Record<ActivityCategory, MessageKey>> = {
  productive: 'catalog.category.productive',
  neutral: 'catalog.category.neutral',
  unproductive: 'catalog.category.unproductive',
  communication: 'catalog.category.communication',
  meeting: 'catalog.category.meeting',
  development: 'catalog.category.development',
  design: 'catalog.category.design',
  accounting: 'catalog.category.accounting',
  sales: 'catalog.category.sales',
  marketing: 'catalog.category.marketing',
  research: 'catalog.category.research',
  training: 'catalog.category.training',
  system: 'catalog.category.system',
  blocked: 'catalog.category.blocked',
  uncategorized: 'catalog.category.uncategorized',
};

const SCOPE_KEY: Readonly<Record<'company' | 'department' | 'job_title', MessageKey>> = {
  company: 'catalog.scope.company',
  department: 'catalog.scope.department',
  job_title: 'catalog.scope.jobTitle',
};

export interface PrivateDomainsProps {
  readonly rules: readonly WebsiteCatalogEntry[];
  readonly total: number;
  readonly categories: readonly ActivityCategory[];
  readonly draft: WebsiteRuleDraft;
  readonly onDraft: (next: WebsiteRuleDraft) => void;
  /** Field names the server refused, so the message lands beside the right input. */
  readonly refused: readonly string[];
  readonly refusalNote?: ReactNode;
}

export function PrivateDomains({
  rules,
  total,
  categories,
  draft,
  onDraft,
  refused,
  refusalNote,
}: PrivateDomainsProps): ReactNode {
  const { t } = useI18n();

  // Private first — the same ordering the employee-facing panel uses.
  const ordered = [...rules].sort((left, right) => {
    const leftPrivate = left.is_private === true ? 0 : 1;
    const rightPrivate = right.is_private === true ? 0 : 1;
    if (leftPrivate !== rightPrivate) return leftPrivate - rightPrivate;
    return left.match_value.localeCompare(right.match_value);
  });

  return (
    <section className="wp-section" aria-labelledby="k5-rules-heading" data-testid="k5-rules">
      <h2 className="wp-section__title" id="k5-rules-heading">
        {t('policies.rules.title')}
      </h2>
      <p className="wp-small wp-muted">{t('policies.rules.intro')}</p>
      <p className="wp-small wp-muted" data-testid="k5-rules-visible">
        {t('policies.rules.visible')}
      </p>

      {ordered.length === 0 ? (
        <EmptyState
          title={t('policies.rules.empty.title')}
          description={t('policies.rules.empty.body')}
          testId="k5-rules-empty"
        />
      ) : (
        <div className="wp-table-scroll">
          <table className="wp-table" data-testid="k5-rules-table">
            <thead>
              <tr>
                <th scope="col">{t('policies.rules.col.host')}</th>
                <th scope="col">{t('policies.rules.col.privacy')}</th>
                <th scope="col">{t('policies.rules.col.category')}</th>
                <th scope="col">{t('policies.rules.col.scope')}</th>
                <th scope="col" className="wp-num">
                  {t('policies.rules.col.version')}
                </th>
              </tr>
            </thead>
            <tbody>
              {ordered.map((rule) => (
                <tr key={rule.id} data-testid={`k5-rule-${rule.id}`}>
                  <th scope="row" className="wp-mono">
                    {rule.match_value}
                  </th>
                  <td>
                    {rule.is_private === true ? (
                      <Badge
                        severity="low"
                        icon="lock"
                        label={t('policies.rules.private')}
                        testId={`k5-rule-private-${rule.id}`}
                      />
                    ) : (
                      <Badge
                        severity="info"
                        icon="info"
                        label={t('policies.rules.named')}
                        testId={`k5-rule-named-${rule.id}`}
                      />
                    )}
                    {rule.is_blacklisted === true ? (
                      <Badge
                        severity="medium"
                        icon="ban"
                        label={t('policies.rules.blacklisted')}
                        testId={`k5-rule-blocked-${rule.id}`}
                      />
                    ) : null}
                  </td>
                  <td>{t(CATEGORY_KEY[rule.category])}</td>
                  <td>{t(SCOPE_KEY[rule.scope_type])}</td>
                  <td className="wp-num" data-testid={`k5-rule-version-${rule.id}`}>
                    {rule.version}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {ordered.length < total ? (
        <p className="wp-small wp-muted" data-testid="k5-rules-truncated">
          {t('policies.rules.truncated', { shown: ordered.length, total })}
        </p>
      ) : null}

      <h3 className="wp-section__title">{t('policies.editor.title')}</h3>
      {refusalNote}

      <div className="wp-filters wp-filters--bar">
        <div className="wp-filters__group">
          <label className="wp-field" htmlFor="k5-editor-host">
            <span className="wp-field__label">{t('policies.editor.host')}</span>
            <input
              id="k5-editor-host"
              className="wp-field__input wp-mono"
              value={draft.match_value}
              aria-describedby="k5-editor-host-hint"
              {...(refused.includes('match_value') ? { 'aria-invalid': true } : {})}
              onChange={(event) => {
                onDraft({ ...draft, match_value: event.target.value });
              }}
              data-testid="k5-editor-host"
            />
          </label>
          <p className="wp-small wp-muted" id="k5-editor-host-hint">
            {t('policies.editor.hostHint')}
          </p>

          <label className="wp-field" htmlFor="k5-editor-match">
            <span className="wp-field__label">{t('policies.editor.matchField')}</span>
            <select
              id="k5-editor-match"
              className="wp-field__input"
              value={draft.match_field}
              onChange={(event) => {
                onDraft({
                  ...draft,
                  match_field: event.target.value === 'domain_suffix' ? 'domain_suffix' : 'domain',
                });
              }}
              data-testid="k5-editor-match"
            >
              <option value="domain">{t('policies.editor.matchField.domain')}</option>
              <option value="domain_suffix">{t('policies.editor.matchField.domain_suffix')}</option>
            </select>
          </label>
        </div>

        <div className="wp-filters__group">
          <label className="wp-field" htmlFor="k5-editor-category">
            <span className="wp-field__label">{t('policies.editor.category')}</span>
            <select
              id="k5-editor-category"
              className="wp-field__input"
              value={draft.category}
              onChange={(event) => {
                const next = categories.find((value) => value === event.target.value);
                if (next !== undefined) onDraft({ ...draft, category: next });
              }}
              data-testid="k5-editor-category"
            >
              {categories.map((category) => (
                <option key={category} value={category}>
                  {t(CATEGORY_KEY[category])}
                </option>
              ))}
            </select>
          </label>

          <label className="wp-toggle" htmlFor="k5-editor-private">
            <input
              id="k5-editor-private"
              type="checkbox"
              checked={draft.is_private}
              aria-describedby="k5-editor-private-hint"
              onChange={(event) => {
                onDraft({ ...draft, is_private: event.target.checked });
              }}
              data-testid="k5-editor-private"
            />
            <span>{t('policies.editor.private')}</span>
          </label>
          <p className="wp-small wp-muted" id="k5-editor-private-hint">
            {t('policies.editor.privateHelp')}
          </p>

          <label className="wp-toggle" htmlFor="k5-editor-blacklist">
            <input
              id="k5-editor-blacklist"
              type="checkbox"
              checked={draft.is_blacklisted}
              onChange={(event) => {
                onDraft({ ...draft, is_blacklisted: event.target.checked });
              }}
              data-testid="k5-editor-blacklist"
            />
            <span>{t('policies.editor.blacklist')}</span>
          </label>
        </div>

        <div className="wp-filters__group">
          <label className="wp-field" htmlFor="k5-editor-scope">
            <span className="wp-field__label">{t('policies.editor.scope')}</span>
            <select
              id="k5-editor-scope"
              className="wp-field__input"
              value={draft.scope_type}
              onChange={(event) => {
                const value = event.target.value;
                const scopeType =
                  value === 'department' || value === 'job_title' ? value : 'company';
                onDraft({
                  ...draft,
                  scope_type: scopeType,
                  // `scope_key` must be present exactly when the scope is not the
                  // company, and the server refuses the disagreement. Cleared here so
                  // a leftover value cannot ride along into a company-scope save.
                  scope_key: scopeType === 'company' ? null : draft.scope_key,
                });
              }}
              data-testid="k5-editor-scope"
            >
              <option value="company">{t('catalog.scope.company')}</option>
              <option value="department">{t('catalog.scope.department')}</option>
              <option value="job_title">{t('catalog.scope.jobTitle')}</option>
            </select>
          </label>

          {draft.scope_type === 'company' ? null : (
            <label className="wp-field" htmlFor="k5-editor-scope-key">
              <span className="wp-field__label">{t('policies.editor.scopeKey')}</span>
              <input
                id="k5-editor-scope-key"
                className="wp-field__input"
                value={draft.scope_key ?? ''}
                {...(refused.includes('scope_key') ? { 'aria-invalid': true } : {})}
                onChange={(event) => {
                  const value = event.target.value.trim();
                  onDraft({ ...draft, scope_key: value === '' ? null : value });
                }}
                data-testid="k5-editor-scope-key"
              />
            </label>
          )}
        </div>
      </div>
    </section>
  );
}
